import { FC, memo } from 'react';

import { ConsoleAPIGraph, CreativeHubTypes } from '@adflow/graphqlgen';
import { AdElement, AdflowAPI } from '@adflow/types';

import type { SoccerFixture } from '../../soccerPreview';
import type { PreviewFontDescriptor } from '../../hooks/useResolvedAdElements';
import { PreviewPresentation } from './PreviewPresentation';
import { PreviewWithResolution } from './PreviewWithResolution';

type Props = {
  elements: Array<AdElement>;
  brand: ConsoleAPIGraph.Brand | null;
  brandKit?: CreativeHubTypes.Brand | null;
  fonts: readonly PreviewFontDescriptor[];
  displayOptions?: AdflowAPI.AdTemplateDisplayOptions | null;
  selectedElementId?: string | null;
  isIndexPreview: boolean;
  forceTopLeftOrigin?: boolean;
  customizations?: Record<string, string> | null;
  svgIdPrefix?: string | null;
  dataSources?: Array<{ id: string; type: string }>;
  activeSoccerFixture?: SoccerFixture | null;
  resolveSoccerCustomAssetUrls?: boolean;
  animation?: AdflowAPI.Animation | null;
  animationPlaybackToken?: number;
  legacyInteractiveSelection?: boolean;
  preResolvedElements?: AdElement[] | null;
  preResolvedAnimationOverrides?: ReadonlyMap<
    string,
    import('@adflow/utils').ElementAnimationStyle
  > | null;
};

const PreviewComponent: FC<Props> = ({
  elements,
  brand,
  brandKit,
  fonts,
  displayOptions,
  selectedElementId,
  isIndexPreview,
  forceTopLeftOrigin,
  customizations,
  svgIdPrefix,
  dataSources,
  activeSoccerFixture,
  resolveSoccerCustomAssetUrls = true,
  animation,
  animationPlaybackToken = 0,
  legacyInteractiveSelection = true,
  preResolvedElements = null,
  preResolvedAnimationOverrides = null,
}) => {
  const presentation = {
    selectedElementId,
    isIndexPreview,
    forceTopLeftOrigin,
    svgIdPrefix,
    legacyInteractiveSelection,
  };

  if (preResolvedElements != null) {
    return (
      <PreviewPresentation
        layoutElements={elements}
        mappedElements={preResolvedElements}
        animationOverrides={preResolvedAnimationOverrides}
        {...presentation}
      />
    );
  }

  return (
    <PreviewWithResolution
      elements={elements}
      brand={brand}
      brandKit={brandKit}
      fonts={fonts}
      displayOptions={displayOptions}
      customizations={customizations}
      dataSources={dataSources}
      activeSoccerFixture={activeSoccerFixture}
      resolveSoccerCustomAssetUrls={resolveSoccerCustomAssetUrls}
      animation={animation}
      animationPlaybackToken={animationPlaybackToken}
      presentation={presentation}
    />
  );
};

PreviewComponent.displayName = 'Preview';

const Preview = memo(PreviewComponent, (prevProps, nextProps) => {
  if (
    prevProps.isIndexPreview !== nextProps.isIndexPreview ||
    prevProps.forceTopLeftOrigin !== nextProps.forceTopLeftOrigin ||
    prevProps.selectedElementId !== nextProps.selectedElementId ||
    prevProps.svgIdPrefix !== nextProps.svgIdPrefix ||
    prevProps.resolveSoccerCustomAssetUrls !== nextProps.resolveSoccerCustomAssetUrls ||
    prevProps.animation !== nextProps.animation ||
    prevProps.animationPlaybackToken !== nextProps.animationPlaybackToken ||
    prevProps.legacyInteractiveSelection !== nextProps.legacyInteractiveSelection ||
    prevProps.preResolvedElements !== nextProps.preResolvedElements ||
    prevProps.preResolvedAnimationOverrides !== nextProps.preResolvedAnimationOverrides
  ) {
    return false;
  }

  if (prevProps.activeSoccerFixture !== nextProps.activeSoccerFixture) {
    return false;
  }

  if (prevProps.brand?._id !== nextProps.brand?._id) {
    return false;
  }

  if (prevProps.displayOptions !== nextProps.displayOptions) {
    return false;
  }

  if (prevProps.dataSources !== nextProps.dataSources) {
    return false;
  }

  if (prevProps.fonts !== nextProps.fonts) {
    if (
      prevProps.fonts.length !== nextProps.fonts.length ||
      prevProps.fonts.some((font, index) => font !== nextProps.fonts[index])
    ) {
      return false;
    }
  }

  if (prevProps.elements !== nextProps.elements) {
    if (
      prevProps.elements.length !== nextProps.elements.length ||
      prevProps.elements.some((el, index) => el !== nextProps.elements[index])
    ) {
      return false;
    }
  }

  const prevCustomizations = prevProps.customizations || {};
  const nextCustomizations = nextProps.customizations || {};
  const prevKeys = Object.keys(prevCustomizations);
  const nextKeys = Object.keys(nextCustomizations);

  if (prevKeys.length !== nextKeys.length) {
    return false;
  }

  for (const key of prevKeys) {
    if (prevCustomizations[key] !== nextCustomizations[key]) {
      return false;
    }
  }

  return true;
});

export default Preview;
export type { Props as PreviewProps };
